// The Players canvas filter scope: the player is the anchor, and competition +
// season are constrained to that player (§7B decision 4). Remembered per section
// via URL + localStorage, independent of the Leagues scope. Picking a player
// seeds comp+season from their most-recent match; the FilterBar then keeps them
// valid against the player's own competition/season lists.

import { useState } from "react";
import { readInitialScopeNumber, persistScopeNumber } from "../lib/scopeParams.js";

const K_PLAYER = "players.playerId";
const K_NAME = "players.playerName"; // display-only (localStorage, not the URL)
const K_COMP = "players.competitionId";
const K_SEASON = "players.seasonId";

function readStoredName(): string | null {
  try {
    return window.localStorage.getItem(K_NAME);
  } catch {
    return null;
  }
}
function persistName(name: string | null): void {
  try {
    if (name === null) window.localStorage.removeItem(K_NAME);
    else window.localStorage.setItem(K_NAME, name);
  } catch {
    /* storage blocked — name is display-only, safe to lose */
  }
}

export interface PlayersScope {
  playerId: number | null;
  playerName: string | null;
  competitionId: number | null;
  seasonId: number | null;
  /** Pick a player + seed their most-recent competition/season (either may be null). */
  selectPlayer: (id: number, name: string, competitionId: number | null, seasonId: number | null) => void;
  setCompetitionId: (id: number) => void;
  setSeasonId: (id: number) => void;
}

export function usePlayersScope(): PlayersScope {
  const [playerId, setPlayerId] = useState<number | null>(() => readInitialScopeNumber(K_PLAYER));
  const [playerName, setPlayerName] = useState<string | null>(() => readStoredName());
  const [competitionId, setComp] = useState<number | null>(() => readInitialScopeNumber(K_COMP));
  const [seasonId, setSeason] = useState<number | null>(() => readInitialScopeNumber(K_SEASON));

  const selectPlayer = (id: number, name: string, comp: number | null, season: number | null) => {
    setPlayerId(id);
    persistScopeNumber(K_PLAYER, id);
    setPlayerName(name);
    persistName(name);
    setComp(comp);
    persistScopeNumber(K_COMP, comp);
    setSeason(season);
    persistScopeNumber(K_SEASON, season);
  };

  const setCompetitionId = (id: number) => {
    setComp(id);
    persistScopeNumber(K_COMP, id);
    // Season only makes sense within a competition — clear it; the FilterBar
    // re-defaults to the player's latest season for the new competition.
    setSeason(null);
    persistScopeNumber(K_SEASON, null);
  };

  const setSeasonId = (id: number) => {
    setSeason(id);
    persistScopeNumber(K_SEASON, id);
  };

  return { playerId, playerName, competitionId, seasonId, selectPlayer, setCompetitionId, setSeasonId };
}
